import React from "react";
import { pipelineSteps, type PipelineStep } from "@veyso/core";

export const STEP_LABELS: Record<PipelineStep, string> = {
  uploaded: "Uploaded",
  "audio-extracted": "Audio extracted",
  transcribed: "Transcript",
  "script-generated": "AI script",
  "actions-detected": "Actions",
  "voice-generated": "Voice",
  "timeline-built": "Timeline",
  ready: "Ready",
};

const STATUS_TONE: Record<string, { label: string; dot: string; text: string }> = {
  draft: { label: "Draft", dot: "bg-ink-400", text: "text-ink-300" },
  uploaded: { label: "Uploaded", dot: "bg-scope-400", text: "text-scope-300" },
  processing: { label: "Processing", dot: "bg-ember-400", text: "text-ember-400" },
  ready: { label: "Ready to edit", dot: "bg-signal-ok", text: "text-signal-ok" },
  rendering: { label: "Rendering", dot: "bg-ember-400", text: "text-ember-400" },
  completed: { label: "Complete", dot: "bg-signal-ok", text: "text-signal-ok" },
  failed: { label: "Failed", dot: "bg-signal-bad", text: "text-signal-bad" },
};

/** A live status reads better as a pulsing record light than as a coloured chip. */
export const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const tone = STATUS_TONE[status] ?? STATUS_TONE.draft;
  const live = status === "processing" || status === "rendering";

  return (
    <span className={`inline-flex items-center gap-2 text-[12px] font-medium ${tone.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${tone.dot} ${live ? "animate-pulse" : ""}`} />
      {tone.label}
    </span>
  );
};

/**
 * Eight thin segments, one per pipeline step. It answers "where is this?" at a
 * glance without a percentage that nobody can act on.
 */
export const PipelineBar: React.FC<{ completed: PipelineStep[]; failed?: boolean }> = ({ completed, failed }) => (
  <div
    className="flex gap-[3px]"
    role="img"
    aria-label={`${completed.length} of ${pipelineSteps.length} steps complete`}
  >
    {pipelineSteps.map((step) => {
      const done = completed.includes(step);
      return (
        <span
          key={step}
          title={STEP_LABELS[step]}
          className={`h-1 w-5 rounded-full transition-colors duration-200 ${
            done ? (failed ? "bg-signal-bad" : "bg-scope-400") : "bg-bay-700"
          }`}
        />
      );
    })}
  </div>
);

export const Spinner: React.FC<{ className?: string }> = ({ className = "" }) => (
  <span
    className={`inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-ink-400/30 border-t-ink-200 ${className}`}
    aria-hidden
  />
);

export const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`eyebrow ${className}`}>{children}</div>
);

export const EmptyState: React.FC<{ title: string; body: string; action?: React.ReactNode }> = ({
  title,
  body,
  action,
}) => (
  <div className="surface flex flex-col items-center gap-3 px-8 py-14 text-center">
    <p className="text-[15px] font-semibold text-ink-100">{title}</p>
    <p className="max-w-sm text-[13px] leading-relaxed text-ink-300">{body}</p>
    {action}
  </div>
);

export const ErrorNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-[6px] border border-signal-bad/30 bg-signal-bad/10 px-3 py-2 text-[12px] text-signal-bad">
    {children}
  </div>
);

/** A labelled control row. Labels are demoted; the value is what the eye needs. */
export const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label,
  hint,
  children,
}) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-[11px] font-medium text-ink-300">{label}</span>
    {children}
    {hint ? <span className="text-[11px] leading-snug text-ink-400">{hint}</span> : null}
  </label>
);

/**
 * A short list of presets plus a free-text box.
 *
 * A bare textbox for something like tone gives no clue what the model responds
 * to, and most people type nothing. Presets make the useful answers visible
 * while the input keeps the field open-ended — these values go straight into the
 * script prompt, so an unusual one is still worth allowing.
 */
/**
 * A row of chips, one of which can be chosen.
 *
 * There was a free-text box under these, and the pair asked a question the
 * chips had already answered: someone who wanted "Clear and friendly" clicked
 * it, and someone who wanted something else had to invent a phrase that shapes
 * a model's writing in a way nobody could predict. The chips are the offer.
 *
 * Nothing is selected by default and clicking the active chip clears it, so the
 * field can be left empty without hunting for a "none" option.
 */
export const PresetChips: React.FC<{
  value: string;
  onChange: (value: string) => void;
  presets: readonly string[];
}> = ({ value, onChange, presets }) => {
  const normalise = (candidate: string) => candidate.trim().toLowerCase();
  const selected = presets.find((preset) => normalise(preset) === normalise(value));

  return (
    <div className="flex flex-wrap gap-1.5">
      {presets.map((preset) => (
        <button
          key={preset}
          type="button"
          className="chip"
          aria-pressed={preset === selected}
          data-selected={preset === selected}
          onClick={() => onChange(preset === selected ? "" : preset)}
        >
          {preset}
        </button>
      ))}
    </div>
  );
};

export const Toggle: React.FC<{
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  hint?: string;
  disabled?: boolean;
}> = ({ label, checked, onChange, hint, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className="flex w-full items-center justify-between gap-3 rounded-[6px] px-1 py-1.5 text-left transition-colors hover:bg-bay-800 disabled:cursor-not-allowed disabled:opacity-50"
  >
    <span className="flex flex-col">
      <span className="text-[12px] font-medium text-ink-200">{label}</span>
      {hint ? <span className="text-[11px] text-ink-400">{hint}</span> : null}
    </span>
    <span
      className={`relative h-[18px] w-[32px] shrink-0 rounded-full transition-colors duration-150 ${
        checked ? "bg-scope-400" : "bg-bay-600"
      }`}
    >
      <span
        className="absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-transform duration-150"
        style={{ transform: `translateX(${checked ? 16 : 2}px)` }}
      />
    </span>
  </button>
);

export const Slider: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix?: string;
  onChange: (value: number) => void;
  disabled?: boolean;
}> = ({ label, value, min, max, step, suffix, onChange, disabled }) => (
  <div className={`flex flex-col gap-1.5 ${disabled ? "opacity-50" : ""}`}>
    <div className="flex items-baseline justify-between">
      <span className="text-[11px] font-medium text-ink-300">{label}</span>
      <span className="tnum text-[12px] font-semibold text-ink-100">
        {value.toFixed(step < 1 ? 2 : 0)}
        {suffix}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(Number(event.target.value))}
      className="h-1 w-full cursor-pointer appearance-none rounded-full bg-bay-600 accent-[var(--color-scope-400)] disabled:cursor-not-allowed"
    />
  </div>
);

export const Select: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}> = ({ value, onChange, options }) => (
  <select className="input cursor-pointer" value={value} onChange={(event) => onChange(event.target.value)}>
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

/**
 * A colour.
 *
 * The native picker rather than a hand-built one: the operating system's is
 * better than anything worth writing here, and it brings eyedropper and recent
 * colours for free. The hex box sits beside it because a brand colour is
 * something people paste, not something they hunt for on a wheel.
 *
 * Typing is accepted character by character but only committed once it forms a
 * real colour, so a half-typed "#2f" does not repaint the preview black.
 */
export const ColorField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => {
  const [draft, setDraft] = React.useState(value);
  React.useEffect(() => setDraft(value), [value]);

  const commit = (next: string) => {
    setDraft(next);
    if (/^#[0-9a-fA-F]{6}$/.test(next.trim())) onChange(next.trim().toLowerCase());
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] font-medium text-ink-300">{label}</span>
      <div className="flex items-center gap-1.5">
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"}
          onChange={(event) => onChange(event.target.value)}
          aria-label={label}
          className="h-[30px] w-[34px] shrink-0 cursor-pointer rounded-[5px] border border-[var(--edge)] bg-bay-900 p-[2px]"
        />
        <input
          className="input tnum !py-1 text-[12px] uppercase"
          value={draft}
          spellCheck={false}
          maxLength={7}
          onChange={(event) => commit(event.target.value)}
        />
      </div>
    </div>
  );
};

export const timecode = (seconds: number): string => {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const minutes = Math.floor(safe / 60);
  const rest = safe % 60;
  return `${minutes}:${rest.toFixed(2).padStart(5, "0")}`;
};
